import {ElMessage, ElMessageBox} from 'element-plus';

export interface BusinessErrorResult {
    error?: { message?: string };
}

function looksLikeHtml(text: string): boolean {
    const trimmed = text.trim();
    return trimmed.startsWith('<') || /<html[\s>]/i.test(trimmed);
}

export function formatUpstreamDetails(details: unknown): string {
    if (details == null) {
        return '';
    }
    if (typeof details === 'string') {
        return details.trim();
    }
    if (typeof details !== 'object') {
        return String(details);
    }
    const parts: string[] = [];
    for (const [k, v] of Object.entries(details as Record<string, unknown>)) {
        if (v == null || v === '') {
            continue;
        }
        parts.push(`${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`);
    }
    return parts.join(', ');
}

/** Prefer upstream.message + details; never prefer error.type over message. */
export function formatUpstreamError(
    upstream: unknown,
    fallback = '上游请求失败',
): string {
    const u = (upstream && typeof upstream === 'object'
        ? upstream
        : {}) as {
        message?: unknown;
        error?: {message?: unknown; type?: unknown; details?: unknown} | string;
    };
    const errObj =
        u.error && typeof u.error === 'object' ? u.error : null;
    const detailsStr = formatUpstreamDetails(errObj?.details);
    const message = String(u.message || errObj?.message || '').trim();
    if (message) {
        return detailsStr ? `${message} (${detailsStr})` : message;
    }
    if (detailsStr) {
        return detailsStr;
    }
    const typeOrErr =
        String(errObj?.type || (typeof u.error === 'string' ? u.error : '') || '').trim();
    if (typeOrErr) {
        return typeOrErr;
    }
    return fallback;
}

export function extractHttpErrorMessage(err: unknown, fallback = '请求失败，请重试'): string {
    const anyErr = err as {
        response?: {
            status?: number;
            data?: {
                message?: string;
                status?: { message?: string; msg?: string };
            } | string;
        };
        message?: string;
    };
    const statusCode = anyErr?.response?.status;
    const data = anyErr?.response?.data;
    const errMsg = anyErr?.message;

    if (data && typeof data === 'object') {
        const nestedUpstream =
            (data as {data?: {upstream?: unknown}}).data?.upstream
            ?? (data as {upstream?: unknown}).upstream;
        if (nestedUpstream && typeof nestedUpstream === 'object') {
            const u = nestedUpstream as {code?: unknown; message?: unknown; error?: unknown};
            const codeNum = u.code !== undefined ? Number(u.code) : NaN;
            const hasSignal =
                (Number.isFinite(codeNum) && codeNum !== 0)
                || String(u.message || '').trim()
                || u.error;
            if (hasSignal) {
                return formatUpstreamError(nestedUpstream, fallback);
            }
        }
        // seal/Tornado 业务错误常用 status.message；未捕获异常常落在 status.msg（含 traceback）
        const statusMsg = data.status?.message || data.status?.msg;
        if (typeof statusMsg === 'string' && statusMsg.trim()) {
            return statusMsg.trim();
        }
        if (typeof data.message === 'string' && data.message.trim()) {
            return data.message.trim();
        }
    }
    if (typeof data === 'string' && data.trim()) {
        const trimmed = data.trim();
        if (looksLikeHtml(trimmed)) {
            return fallback;
        }
        return trimmed;
    }
    if (typeof errMsg === 'string' && errMsg.trim()
        && !/^Request failed with status code \d+$/.test(errMsg.trim())) {
        return errMsg.trim();
    }
    if (statusCode === 404) {
        return '接口不存在（404）。请确认已部署最新后端并重启 API 服务后再试';
    }
    if (statusCode === 401) {
        return '未登录或登录已失效，请重新登录';
    }
    if (statusCode === 403) {
        return '没有权限执行此操作（需要 admin）';
    }
    if (statusCode === 400) {
        return fallback;
    }
    return fallback;
}

/** 从长 traceback 里抽出最后一行异常类型，方便 Toast 一眼看懂 */
export function summarizeErrorMessage(message: string, maxLen = 160): string {
    const lines = message.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const lastExc = [...lines].reverse().find((l) => /^[A-Za-z_.]+(?:Error|Exception|Timeout)\b/.test(l));
    const summary = lastExc || lines[lines.length - 1] || message;
    if (summary.length <= maxLen) {
        return summary;
    }
    return `${summary.slice(0, maxLen - 1)}…`;
}

export function showBusinessError(
    result: BusinessErrorResult | null | undefined,
    fallback = '操作失败',
) {
    const message = result?.error?.message || fallback;
    ElMessage.error(message);
}

export function showHttpError(err: unknown, fallback = '请求失败，请重试') {
    ElMessage.error(extractHttpErrorMessage(err, fallback));
}

/** 登录等场景：短 Toast + 可复制的完整错误弹窗（含 traceback） */
export function showHttpErrorDetail(
    err: unknown,
    title = '请求失败',
    fallback = '请求失败，请重试',
) {
    const message = extractHttpErrorMessage(err, fallback);
    ElMessage.error(summarizeErrorMessage(message));
    return ElMessageBox.alert(message, title, {
        type: 'error',
        confirmButtonText: '关闭',
        customClass: 'http-error-detail-box',
        distinguishCancelAndClose: true,
    }).catch(() => undefined);
}
